import type { CompanyQuestion } from '../types/game';

/**
 * Human-editable text for company questions (matches preview + email style).
 *
 * 1. Question text
 * A. Option one
 * B. Option two ✓
 * C. Option three
 * D. Option four
 * Fun fact: optional
 */
export function serializeCompanyQuestionsForEdit(questions: CompanyQuestion[]): string {
  return questions
    .map((q, i) => {
      const opts = (q.options?.length ? q.options : [q.correctAnswer].filter(Boolean)).slice(
        0,
        4
      );
      const correct = (q.correctAnswer || '').trim();
      const correctLower = correct.toLowerCase();
      const matched =
        opts.find((opt) => opt === correct) ||
        opts.find((opt) => opt.toLowerCase() === correctLower);
      const optionLines = opts
        .map((opt, oi) => {
          const letter = String.fromCharCode(65 + oi);
          const mark = matched && opt === matched ? ' ✓' : '';
          return `${letter}. ${opt}${mark}`;
        })
        .join('\n');
      // Always include Answer: when known so round-trip stays valid even if ✓ is edited away.
      const answerLine = matched ? `\nAnswer: ${matched}` : correct ? `\nAnswer: ${correct}` : '';
      const fact = q.interesting?.trim() ? `\nFun fact: ${q.interesting.trim()}` : '';
      return `${i + 1}. ${q.question}\n${optionLines}${answerLine}${fact}`;
    })
    .join('\n\n');
}

export type ParseCompanyQuestionsResult =
  | { ok: true; questions: CompanyQuestion[] }
  | { ok: false; message: string; index?: number };

const OPTION_LINE = /^([A-Da-d])\.\s*(.*)$/;
const ANSWER_LINE = /^(?:Answer|Correct)\s*:\s*(.+)$/i;
const FACT_LINE = /^(?:Fun fact|Interesting)\s*:\s*(.+)$/i;
const EXPECTED_LETTERS = 'ABCD';
/** New question block: "1. Text…" (requires a space so "1.A" option typos stay BAD_LINE). */
const NUMBERED_QUESTION_START = /^\d+\.\s+\S/;

/** Normalize newlines and split into question blocks (blank lines optional). */
export function splitCompanyQuestionBlocks(text: string): string[] {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  if (!normalized) return [];

  const lines = normalized.split('\n');
  const blocks: string[] = [];
  let current: string[] = [];

  for (const raw of lines) {
    const trimmed = raw.trim();
    if (!trimmed) continue;

    if (NUMBERED_QUESTION_START.test(trimmed) && current.length > 0) {
      blocks.push(current.join('\n'));
      current = [trimmed];
      continue;
    }
    current.push(trimmed);
  }

  if (current.length > 0) {
    blocks.push(current.join('\n'));
  }
  return blocks;
}

/**
 * Parse edited text back into CompanyQuestion[].
 * Preserves id / points / timeLimit / etc. from `previous` by matching index when possible.
 */
export function parseCompanyQuestionsEdit(
  text: string,
  previous: CompanyQuestion[] = []
): ParseCompanyQuestionsResult {
  const blocks = splitCompanyQuestionBlocks(text);

  if (blocks.length === 0) {
    return { ok: true, questions: [] };
  }

  const parsed: CompanyQuestion[] = [];
  const now = Date.now();

  for (let bi = 0; bi < blocks.length; bi++) {
    const lines = blocks[bi]
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    if (lines.length === 0) continue;

    let questionLine = lines[0];
    const numbered = questionLine.match(/^\d+\.\s*(.+)$/);
    if (numbered) questionLine = numbered[1].trim();
    if (!questionLine) {
      return { ok: false, message: 'EMPTY_QUESTION', index: bi };
    }
    if (questionLine.length > 300) {
      return { ok: false, message: 'QUESTION_TOO_LONG', index: bi };
    }

    const options: string[] = [];
    const letters: string[] = [];
    let correctFromMark: string | null = null;
    let correctFromAnswerLine: string | null = null;
    let interesting = '';

    for (let li = 1; li < lines.length; li++) {
      const line = lines[li];
      const optMatch = line.match(OPTION_LINE);
      if (optMatch) {
        if (options.length >= 4) {
          return { ok: false, message: 'TOO_MANY_OPTIONS', index: bi };
        }
        const letter = optMatch[1].toUpperCase();
        let opt = optMatch[2].trim();
        const marked = /\s*[✓✔*]\s*$/.test(opt);
        if (marked) {
          opt = opt.replace(/\s*[✓✔*]\s*$/, '').trim();
          correctFromMark = opt;
        }
        if (!opt) {
          return { ok: false, message: 'EMPTY_OPTION', index: bi };
        }
        letters.push(letter);
        options.push(opt);
        continue;
      }
      const answerMatch = line.match(ANSWER_LINE);
      if (answerMatch) {
        correctFromAnswerLine = answerMatch[1].trim();
        continue;
      }
      const factMatch = line.match(FACT_LINE);
      if (factMatch) {
        interesting = factMatch[1].trim();
        continue;
      }
      // Bare fun-fact line only after A–D are present
      if (options.length >= 4 && !interesting && !ANSWER_LINE.test(line)) {
        interesting = line;
        continue;
      }
      return { ok: false, message: 'BAD_LINE', index: bi };
    }

    if (letters.join('') !== EXPECTED_LETTERS) {
      return { ok: false, message: 'BAD_OPTION_LETTERS', index: bi };
    }
    if (options.length !== 4) {
      return { ok: false, message: 'NEED_FOUR_OPTIONS', index: bi };
    }
    if (options.some((o) => o.length > 120)) {
      return { ok: false, message: 'OPTION_TOO_LONG', index: bi };
    }
    if (new Set(options.map((o) => o.toLowerCase())).size !== 4) {
      return { ok: false, message: 'DUPLICATE_OPTIONS', index: bi };
    }
    if (interesting.length > 400) {
      return { ok: false, message: 'INTERESTING_TOO_LONG', index: bi };
    }

    const correct =
      (correctFromMark && options.includes(correctFromMark) ? correctFromMark : null) ||
      (correctFromAnswerLine && options.includes(correctFromAnswerLine)
        ? correctFromAnswerLine
        : null) ||
      (correctFromAnswerLine
        ? options.find((o) => o.toLowerCase() === correctFromAnswerLine!.toLowerCase())
        : null);

    if (!correct) {
      return { ok: false, message: 'MARK_CORRECT', index: bi };
    }

    const prev = previous[bi];
    parsed.push({
      id: prev?.id || `company-edit-${now}-${bi}`,
      question: questionLine,
      options,
      correctAnswer: correct,
      points: prev?.points ?? 100,
      timeLimit: prev?.timeLimit ?? 20000,
      category: prev?.category ?? 'company',
      source: prev?.source ?? 'ai',
      generatedAt: prev?.generatedAt ?? now,
      ...(interesting ? { interesting } : {}),
    });
  }

  return { ok: true, questions: parsed };
}

/** i18n key + params for a failed parse / live format check. */
export function companyQuestionsEditErrorKey(result: Extract<ParseCompanyQuestionsResult, { ok: false }>): {
  key: string;
  n: number | string;
} {
  const n = typeof result.index === 'number' ? result.index + 1 : '?';
  const keyByCode: Record<string, string> = {
    EMPTY_QUESTION: 'lobby.settings.companyQuestions.editEmptyQuestion',
    QUESTION_TOO_LONG: 'lobby.settings.companyQuestions.editQuestionTooLong',
    NEED_FOUR_OPTIONS: 'lobby.settings.companyQuestions.editNeedFourOptions',
    TOO_MANY_OPTIONS: 'lobby.settings.companyQuestions.editTooManyOptions',
    BAD_OPTION_LETTERS: 'lobby.settings.companyQuestions.editBadOptionLetters',
    EMPTY_OPTION: 'lobby.settings.companyQuestions.editEmptyOption',
    BAD_LINE: 'lobby.settings.companyQuestions.editBadLine',
    OPTION_TOO_LONG: 'lobby.settings.companyQuestions.editOptionTooLong',
    DUPLICATE_OPTIONS: 'lobby.settings.companyQuestions.editDuplicateOptions',
    INTERESTING_TOO_LONG: 'lobby.settings.companyQuestions.editInterestingTooLong',
    MARK_CORRECT: 'lobby.settings.companyQuestions.editMarkCorrect',
  };
  return {
    key: keyByCode[result.message] || 'lobby.settings.companyQuestions.editParseError',
    n,
  };
}
